import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов ИМТ
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение показателей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться ваши сохраненные расчеты ИМТ для сравнения. 
          Нажмите "Сохранить расчет" после вычисления, чтобы добавить результат для сравнения.
        </p>
      </div>
    );
  }

  // Сортировка расчетов по дате (сначала новые)
  const sortedCalculations = [...savedCalculations].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение показателей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'запись', 'записи', 'записей')}
        </span>
      </div>

      <div className="space-y-4">
        {sortedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>ИМТ:</span>
                <span className="font-medium">
                  {formatNumber(calc.results.bmi, 1)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Категория:</span>
                <span className={`font-medium ${getBmiCategoryColor(calc.results.category)}`}>
                  {getShortCategory(calc.results.category)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Параметры:</span>
                <span>
                  {calc.formData.height} см, {calc.formData.weight} кг
                </span>
              </div>
              <div className="flex justify-between">
                <span>Дата расчета:</span>
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  {formatDate(calc.date)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length >= 2 && (
        <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">Сравнительный анализ</h4>
          <div className="space-y-2 text-xs text-emerald-600 dark:text-emerald-400">
            {compareCalculations(sortedCalculations)}
          </div>
        </div>
      )}
    </div>
  );
};

// Получить короткую версию категории
const getShortCategory = (category) => {
  if (category.includes('Выраженный дефицит')) return 'Выраж. дефицит';
  if (category.includes('Недостаточная')) return 'Недост. вес';
  if (category.includes('Нормальная')) return 'Норма';
  if (category.includes('Избыточная')) return 'Избыт. вес';
  if (category.includes('Ожирение I')) return 'Ожирение I';
  if (category.includes('Ожирение II')) return 'Ожирение II';
  if (category.includes('Ожирение III')) return 'Ожирение III';
  return category;
};

// Получить цвет для категории ИМТ
const getBmiCategoryColor = (category) => {
  if (category.includes('дефицит')) return 'text-blue-600 dark:text-blue-400';
  if (category.includes('Нормальная')) return 'text-green-600 dark:text-green-400';
  if (category.includes('Избыточная')) return 'text-amber-600 dark:text-amber-400';
  if (category.includes('Ожирение I')) return 'text-orange-600 dark:text-orange-400';
  if (category.includes('Ожирение II') || category.includes('Ожирение III')) {
    return 'text-red-600 dark:text-red-400';
  }
  return '';
};

// Форматирование даты
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ru-RU', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Склонение в зависимости от числа
const pluralize = (count, one, few, many) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  
  if (mod10 === 1 && mod100 !== 11) {
    return one;
  } else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return few;
  } else {
    return many;
  }
};

// Сравнение расчетов
const compareCalculations = (calculations) => {
  if (calculations.length < 2) return null;
  
  // Получаем два последних расчета
  const latest = calculations[0];
  const previous = calculations[1];
  
  // Разница в ИМТ
  const bmiDiff = latest.results.bmi - previous.results.bmi;
  
  // Разница в весе
  const weightDiff = parseFloat(latest.formData.weight) - parseFloat(previous.formData.weight);
  
  // Изменение категории
  const categoryChanged = latest.results.category !== previous.results.category;
  
  // Формируем сообщения
  const messages = [];
  
  if (Math.abs(bmiDiff) > 0.1) {
    messages.push(
      <p key="bmi">
        ИМТ {bmiDiff > 0 ? 'увеличился' : 'уменьшился'} на {Math.abs(bmiDiff).toFixed(1)} единиц 
        с {previous.results.bmi.toFixed(1)} до {latest.results.bmi.toFixed(1)}.
      </p>
    );
  } else {
    messages.push(<p key="bmi">ИМТ практически не изменился.</p>);
  }
  
  if (Math.abs(weightDiff) > 0.1) {
    messages.push(
      <p key="weight">
        Вес {weightDiff > 0 ? 'увеличился' : 'уменьшился'} на {Math.abs(weightDiff).toFixed(1)} кг 
        с {parseFloat(previous.formData.weight).toFixed(1)} до {parseFloat(latest.formData.weight).toFixed(1)} кг.
      </p>
    );
  }
  
  if (categoryChanged) {
    messages.push(
      <p key="category">
        Категория изменилась с "{getShortCategory(previous.results.category)}" на "{getShortCategory(latest.results.category)}".
      </p>
    );
  }
  
  // Проверка динамики изменений
  const recentCalculations = calculations.slice(0, Math.min(5, calculations.length));
  if (recentCalculations.length >= 3) {
    const trend = analyzeTrend(recentCalculations);
    if (trend) {
      messages.push(<p key="trend">{trend}</p>);
    }
  }
  
  return messages;
};

// Анализ тренда изменений ИМТ
const analyzeTrend = (calculations) => {
  // Получаем значения ИМТ от новых к старым
  const bmiValues = calculations.map(calc => calc.results.bmi);
  
  let increasing = true;
  let decreasing = true;
  
  // Проверяем тренд
  for (let i = 0; i < bmiValues.length - 1; i++) {
    if (bmiValues[i] <= bmiValues[i + 1]) {
      increasing = false;
    }
    if (bmiValues[i] >= bmiValues[i + 1]) {
      decreasing = false;
    }
  }
  
  // Формируем сообщение о тренде
  if (increasing) {
    return "Наблюдается стабильное снижение ИМТ. Продолжайте в том же духе!";
  } else if (decreasing) {
    return "Наблюдается тенденция к увеличению ИМТ. Обратите внимание на питание и физическую активность.";
  } else {
    // Рассчитываем общее изменение
    const firstBmi = bmiValues[bmiValues.length - 1];
    const lastBmi = bmiValues[0];
    const totalChange = lastBmi - firstBmi;
    
    if (Math.abs(totalChange) < 0.3) {
      return "ИМТ остается стабильным в последних измерениях.";
    } else if (totalChange > 0) {
      return "Общая тенденция к увеличению ИМТ, но с колебаниями.";
    } else {
      return "Общая тенденция к снижению ИМТ, но с колебаниями.";
    }
  }
};

export default ComparisonSidebar;